import { ComponentProps, FC, MouseEventHandler, useRef, useState } from 'react'
import { useHover, useMergedRef } from '@mantine/hooks'
import { TrackingLabel } from '../types/tracking'
import { WithClassName } from '../types/utils'
import { Icon, type IconName, type IconSize } from './Icon'
import { Colors } from '../style/colors'
import { ElevationLevel, Sizes } from '../style/tokens'

type ButtonVariant = 'primary' | 'secondary' | 'negative' | 'tertiary'
type ButtonSize = 's' | 'm'

type MouseEventInfo = {
  type?: MouseEvent['type']
  isRippling: boolean
  x: number
  y: number
  height: number
  width: number
}

export type ButtonProps = WithClassName &
  ComponentProps<'button'> & {
    trackingLabel: TrackingLabel
    variant?: ButtonVariant
    iconName?: IconName
    iconSize?: IconSize
    size?: ButtonSize
    hasChevron?: boolean
  }

const BUTTON_GAP: Record<ButtonSize, string> = {
  s: '6px',
  m: '4px',
}

const getButtonSize = (variant: ButtonVariant, size: ButtonSize) => {
  switch (size) {
    case 's':
      return {
        padding: '6px 8px',
        fontSize: '14px',
      }
    case 'm':
      if (variant === 'tertiary') {
        return {
          padding: '6px 12px',
        }
      }
      return {
        padding: '9px 11px',
      }
  }
}

type ButtonStates = 'default' | 'hover' | 'active'

const ButtonVariantBackground: { [key in ButtonVariant]: { [prop in ButtonStates]: React.CSSProperties } } = {
  primary: {
    active: { backgroundColor: Colors.Navy },
    default: { backgroundColor: Colors.King },
    hover: { backgroundColor: Colors.Cobalt },
  },
  secondary: {
    active: { backgroundColor: Colors.Navy },
    default: { backgroundColor: Colors.White },
    hover: { backgroundColor: Colors.Topaze },
  },
  tertiary: {
    active: { backgroundColor: Colors.Navy },
    default: { backgroundColor: 'transparent' },
    hover: { backgroundColor: Colors.Topaze },
  },
  negative: {
    active: { backgroundColor: Colors.Cherry },
    default: { backgroundColor: Colors.White },
    hover: { backgroundColor: Colors.Mew },
  },
}

const ButtonVariantDefaultTextColor: { [key in ButtonVariant]: React.CSSProperties } = {
  primary: { color: Colors.White },
  secondary: { color: Colors.King },
  tertiary: { color: Colors.King },
  negative: { color: Colors.Alert },
}

const ButtonVariantStyles: { [key in ButtonVariant]: React.CSSProperties } = {
  primary: {
    ...ButtonVariantBackground['primary'].default,
    ...ButtonVariantDefaultTextColor['primary'],
  },
  secondary: {
    ...ButtonVariantBackground['secondary'].default,
    ...ButtonVariantDefaultTextColor['secondary'],
    borderColor: Colors.King,
  },
  tertiary: {
    ...ButtonVariantDefaultTextColor['tertiary'],
    ...ButtonVariantBackground['tertiary'].default,
    borderColor: 'transparent',
  },
  negative: {
    ...ButtonVariantDefaultTextColor['negative'],
    ...ButtonVariantBackground['negative'].default,
    borderColor: Colors.Alert,
  },
}

const iconButtonStyle = ({ iconSize, variant }: { iconSize: string; variant: ButtonVariant }) => {
  const t =
    variant === 'tertiary'
      ? {
          border: 'none',
          borderRadius: '50%',
          padding: '9px 6px 4px 6px',
        }
      : {}

  return {
    padding: '9px',
    height: iconSize,
    width: iconSize,
    ...t,
  }
}

export const Button: FC<ButtonProps> = ({ trackingLabel, variant = 'primary', children, className, iconName, iconSize, type = 'button', size = 'm', hasChevron = false, style, ...props }) => {
  const iconOnly = !!iconName && !children

  const { hovered, ref: hoverRef } = useHover<HTMLButtonElement>()
  const buttonRef = useRef<HTMLButtonElement>(null)

  const [mouseEventInfo, setMouseEventInfo] = useState<MouseEventInfo>({
    isRippling: false,
    x: 0,
    y: 0,
    height: 0,
    width: 0,
  })

  const defaultIconSize = size === 's' ? Sizes[16] : iconOnly ? Sizes[26] : Sizes[20]

  const mergedRef = useMergedRef(buttonRef, hoverRef)

  const handleMouseEvent: MouseEventHandler<HTMLButtonElement> = (event) => {
    const rect = buttonRef.current?.getBoundingClientRect()

    if (rect && !mouseEventInfo.isRippling) {
      setMouseEventInfo({
        type: event.type,
        isRippling: true,
        x: event.clientX - rect.x,
        y: event.clientY - rect.y,
        height: rect.height,
        width: rect.width,
      })

      setTimeout(() => {
        setMouseEventInfo({
          type: event.type,
          isRippling: false,
          x: 0,
          y: 0,
          height: 0,
          width: 0,
        })
      }, 250)
    }
  }

  const rippleDuration = 250

  const clickStyle = mouseEventInfo.isRippling
    ? {
        overflow: 'hidden' as const,
        ...(mouseEventInfo.type === 'mousedown' ? { color: Colors.White } : ButtonVariantDefaultTextColor[variant]),
      }
    : {}

  // Plain inline `style` props can't express `::after`/`&` selectors, so the ripple circle is a real
  // element rather than a pseudo-element — otherwise it silently never renders.
  const rippleSpanStyle: React.CSSProperties | undefined = mouseEventInfo.isRippling
    ? {
        display: 'block',
        position: 'absolute',
        top: mouseEventInfo.y - mouseEventInfo.width,
        left: mouseEventInfo.x - mouseEventInfo.width,
        height: mouseEventInfo.width * 2,
        width: mouseEventInfo.width * 2,
        borderRadius: '100%',
        pointerEvents: 'none',
        animation: `ripple-animation ${rippleDuration}ms ease-in`,
        ...(mouseEventInfo.type === 'mousedown' ? ButtonVariantBackground[variant].active : ButtonVariantBackground[variant].hover),
      }
    : undefined

  // While rippling, the ripple `<span>` (last in the DOM, absolutely positioned) would otherwise render
  // above the icon/label — lift them into their own stacking context so they stay visible on top of it.
  const childContentStyle: React.CSSProperties = {
    pointerEvents: 'none',
    ...(mouseEventInfo.isRippling ? { position: 'relative', zIndex: ElevationLevel.low.zIndex } : {}),
  }

  const buttonStyle = {
    ...getButtonSize(variant, size),
    fontWeight: 'bold',
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: '4px',
    ...ButtonVariantStyles[variant],
    ...(hovered && !props.disabled
      ? {
          ...ButtonVariantBackground[variant].hover,
        }
      : {}),

    ...(iconOnly
      ? iconButtonStyle({ iconSize: defaultIconSize, variant })
      : {
          display: 'inline-flex',
          alignItems: 'center',
          gap: BUTTON_GAP[size],
        }),

    boxSizing: 'content-box' as const,
    transitionProperty: 'color, background-color',
    transitionDuration: `${rippleDuration / 4}ms, ${rippleDuration / 2}ms`,
    transitionDelay: `0ms, ${rippleDuration / 2}ms`,
    position: 'relative' as const,

    ...(props.disabled
      ? {
          color: Colors.Silver,
          backgroundColor: variant === 'tertiary' ? 'transparent' : Colors.Platinum,
          cursor: 'not-allowed',
          borderColor: 'transparent',
        }
      : {}),
  }

  return (
    <>
      <style>
        {`
          @keyframes ripple-animation {
            0% { opacity: 0; transform: scale(0); }
            50% { transform: scale(0.6); opacity: 1; }
            100% { transform: scale(1.6); opacity: 0.2; }
          }
        `}
      </style>

      <button
        ref={mergedRef}
        type={type}
        data-tracking={trackingLabel}
        onMouseEnter={handleMouseEvent}
        onMouseDown={handleMouseEvent}
        className={className}
        {...props}
        style={{ ...buttonStyle, ...clickStyle, ...style }}
      >
        {iconName && <Icon size={iconSize ?? 'md'} name={iconName} className="btn-icon" style={childContentStyle} />}
        {children && <span style={childContentStyle}>{children}</span>}
        {hasChevron && <Icon size={iconSize ?? 'md'} name="chevron_down" style={childContentStyle} />}
        {rippleSpanStyle && <span style={rippleSpanStyle} />}
      </button>
    </>
  )
}
